import { useErrorDetailDrawerContent } from '@/features/import_export_histories/Drawer/hooks/useErrorDetailDrawerContent';
import { useTranslation } from '@/i18n';
import {
  ActionMenu,
  Caption,
  Table,
  type TableProps,
} from '@moneyforward/ap-frontend-components';
import {
  GetImportErrorMessagesResponse,
  GetImportHistoryErrorsCsvDownloadsCsvEncoding,
} from 'ap-openapi';
import classnames from 'classnames/bind';
import { formatDateWithTimeNoSecond } from 'date-util';
import { FC, memo, useMemo } from 'react';
import styles from './ErrorDetailDrawer.module.scss';
import {
  useErrorDetailDownloadMenu,
  type UseErrorDetailDownloadMenuResult,
} from './hooks/useErrorDetailDownloadMenu';

const ERROR_THRESHOLD = 100;

const cx = classnames.bind(styles);

type ErrorDetailColumn = TableProps<ErrorDetailTableRow>['columns'][number];
export type ErrorDetailRow = {
  lineNumber: number;
  content: string;
};

export type ErrorDetailTableRow = ErrorDetailRow & { id: string };

type Props = {
  submittedAt?: string;
  finishedAt?: string;
  errors: GetImportErrorMessagesResponse['import_error_messages'];
  onDownload?: (
    encoding: GetImportHistoryErrorsCsvDownloadsCsvEncoding
  ) => void;
};

export const InnerErrorDetailDrawerContent: FC<Props> = memo(
  ({ submittedAt, finishedAt, errors, onDownload }) => {
    const { t } = useTranslation();
    const {
      downloadItems,
      downloadButtonProps,
    }: UseErrorDetailDownloadMenuResult = useErrorDetailDownloadMenu({
      onDownload,
    });

    const wasInterrupted = errors && errors.length >= ERROR_THRESHOLD;

    const tableRows = useMemo(
      (): ErrorDetailTableRow[] =>
        (errors || [])?.map((error, index) => ({
          lineNumber: error.row ?? 0,
          content: error.messages?.join('\n') ?? '',
          id: `${error.row ?? '_' + index}`,
        })),
      [errors]
    );

    const columns: ErrorDetailColumn[] = useMemo(
      () => [
        {
          title: t('import_export_history_error_detail_column_line_number'),
          dataIndex: 'lineNumber',
          key: 'lineNumber',
          width: 100,
          render: (lineNumber: number) =>
            t('import_export_history_error_detail_line_display', {
              number: lineNumber,
            }),
        },
        {
          title: t('import_export_history_error_detail_column_error_content'),
          dataIndex: 'content',
          key: 'content',
        },
      ],
      [t]
    );

    const tablePagination = useMemo(
      () => ({
        currentPage: 1,
        currentPerPage: tableRows.length,
        maxPage: 1,
        mode: 'label' as const,
      }),
      [tableRows.length]
    );

    return (
      <div className={cx('container')}>
        <div className={cx('header-container')}>
          <div className={cx('header-row')}>
            <span className={cx('header-label')}>
              {t('export_files_executed_at')}:
            </span>
            <span>{formatDateWithTimeNoSecond(submittedAt)}</span>
          </div>
          <div className={cx('header-row')}>
            <span className={cx('header-label')}>
              {t('export_files_finished_at')}:
            </span>
            <span>{formatDateWithTimeNoSecond(finishedAt)}</span>
          </div>
        </div>

        <p className={cx('message')}>
          {t('import_export_history_error_detail_default_message')}
        </p>

        <div
          className={cx('error-count-row', {
            'error-count-row--with-alert': errors && errors.length,
          })}
        >
          <div>
            <p className={cx('error-count')}>
              {t('import_export_history_error_detail_error_count', {
                count: errors?.length,
              })}
            </p>
            {!!wasInterrupted && (
              <div className={cx('alert')}>
                <Caption type='error'>
                  {t(
                    'import_export_history_error_detail_validation_interrupted'
                  )}
                </Caption>
              </div>
            )}
          </div>
          {onDownload && downloadItems.length > 0 && (
            <ActionMenu
              type='button'
              buttonProps={downloadButtonProps}
              items={downloadItems}
            />
          )}
        </div>

        <div className={cx('table-wrap')}>
          <Table<ErrorDetailTableRow>
            legacy={false}
            className={cx('error-detail-table')}
            columns={columns}
            data={tableRows}
            rowKey='id'
            pagination={tablePagination}
            emptyType='object'
            hideHeaderPagination
            hideFooterPagination
          />
        </div>
      </div>
    );
  }
);

InnerErrorDetailDrawerContent.displayName = 'InnerErrorDetailDrawerContent';

export const ErrorDetailDrawerContent: FC<{
  id: string;
}> = memo(({ id }) => {
  const { submittedAt, finishedAt, errors, downloadFile } =
    useErrorDetailDrawerContent(id);

  return (
    <InnerErrorDetailDrawerContent
      submittedAt={submittedAt}
      finishedAt={finishedAt}
      errors={errors}
      onDownload={downloadFile}
    />
  );
});
ErrorDetailDrawerContent.displayName = 'ErrorDetailDrawerContent';
